import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LOGO } from "@/lib/catalog";
import { LogOut, RefreshCw, Trash2, Check, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (s) =>
  new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminOrders() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("brownies"); // 'brownies' | 'receita'
  const [orders, setOrders] = useState([]);
  const [recipeOrders, setRecipeOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const token = localStorage.getItem("tqb_admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: headers() }),
        axios.get(`${API}/recipe/admin/orders`, { headers: headers() }),
      ]);
      setOrders(a.data);
      setRecipeOrders(b.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tqb_admin_token");
        navigate("/admin/login");
        return;
      }
      toast.error("Falha ao carregar pedidos");
    } finally {
      setLoading(false);
    }
  }, [headers, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("tqb_admin_token")) {
      navigate("/admin/login");
      return;
    }
    fetchOrders();
  }, [fetchOrders, navigate]);

  const logout = () => {
    localStorage.removeItem("tqb_admin_token");
    navigate("/admin/login");
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este pedido?")) return;
    try {
      await axios.delete(`${API}/admin/orders/${id}`, { headers: headers() });
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("Pedido excluído");
    } catch {
      toast.error("Falha ao excluir");
    }
  };

  const approveRecipe = async (id) => {
    try {
      const { data } = await axios.post(`${API}/recipe/admin/orders/${id}/approve`, {}, { headers: headers() });
      setRecipeOrders((prev) => prev.map((o) => o.id === id ? { ...o, status: "approved", download_token: data.download_token } : o));
      const link = `${API}/recipe/download/${data.download_token}`;
      try { await navigator.clipboard.writeText(link); toast.success("Pedido liberado. Link do PDF copiado."); }
      catch { toast.success("Pedido liberado."); }
    } catch {
      toast.error("Falha ao aprovar");
    }
  };

  const copyRecipeLink = async (token) => {
    const link = `${API}/recipe/download/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link do PDF copiado");
    } catch {
      toast.error("Falha ao copiar");
    }
  };

  const removeRecipe = async (id) => {
    if (!window.confirm("Excluir este pedido de receita?")) return;
    try {
      await axios.delete(`${API}/recipe/admin/orders/${id}`, { headers: headers() });
      setRecipeOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("Excluído");
    } catch {
      toast.error("Falha ao excluir");
    }
  };

  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const recipeRevenue = recipeOrders.filter((o) => o.status === "approved").reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <main data-testid="admin-orders-page" className="min-h-screen bg-white text-black">
      <header className="border-b border-black sticky top-0 bg-white z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="The Quick Brownie" className="h-9 md:h-10" />
            <span className="font-display text-lg md:text-xl">The Quick Brownie</span>
          </Link>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={fetchOrders} className="btn-outline !px-4 !py-2 text-xs">
              <RefreshCw size={14} /> Atualizar
            </button>
            <button data-testid="admin-logout" onClick={logout} className="btn-primary !px-4 !py-2 text-xs">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-12 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em]">Painel</p>
            <h1 className="font-display text-4xl md:text-6xl mt-3 tracking-[-0.02em]">Pedidos</h1>
          </div>
          <div className="flex gap-10">
            <Stat label="Brownies" value={orders.length} />
            <Stat label="Receita (brownies)" value={fmtBRL(totalRevenue)} />
            <Stat label="PDF vendidos" value={recipeOrders.filter((o) => o.status === "approved").length} />
            <Stat label="Receita (PDF)" value={fmtBRL(recipeRevenue)} />
          </div>
        </div>

        <div className="mt-10 border-b border-black flex gap-0">
          <button
            data-testid="tab-brownies"
            onClick={() => setTab("brownies")}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-r border-black ${tab === "brownies" ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
          >
            Brownies ({orders.length})
          </button>
          <button
            data-testid="tab-receita"
            onClick={() => setTab("receita")}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-r border-black ${tab === "receita" ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
          >
            Receita PDF ({recipeOrders.length})
            {recipeOrders.filter((o) => o.status === "pending").length > 0 && (
              <span className="ml-2 inline-block w-2 h-2 bg-current rounded-full" />
            )}
          </button>
        </div>

        {loading ? (
          <p className="py-20 text-center text-sm uppercase tracking-[0.2em]">Carregando...</p>
        ) : tab === "brownies" ? (
          orders.length === 0 ? (
            <p data-testid="admin-empty" className="py-20 text-center text-sm uppercase tracking-[0.2em]">Nenhum pedido até o momento.</p>
          ) : (
            <div className="mt-8 border border-black overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-orders-table">
                <thead className="bg-black text-white">
                  <tr>
                    <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">Data</th>
                    <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">Cliente</th>
                    <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">WhatsApp</th>
                    <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">Pedido</th>
                    <th className="text-right p-3 font-medium uppercase tracking-[0.18em] text-xs">Total</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-black align-top">
                      <td className="p-3 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      <td className="p-3 font-medium">{o.name}</td>
                      <td className="p-3 whitespace-nowrap">
                        <a href={`https://wa.me/${o.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="underline underline-offset-4">
                          {o.phone}
                        </a>
                      </td>
                      <td className="p-3">
                        {o.order_type === "unit" ? (
                          <span><strong>{o.quantity}× </strong>{o.flavor}</span>
                        ) : (
                          <span>
                            <strong>Kit 5: </strong>
                            {Object.entries(o.kit_breakdown || {}).map(([f, q]) => `${q}× ${f}`).join(" · ")}
                          </span>
                        )}
                        {o.notes && <div className="text-xs mt-1 opacity-70">Obs: {o.notes}</div>}
                      </td>
                      <td className="p-3 text-right font-display text-lg whitespace-nowrap">{fmtBRL(o.total)}</td>
                      <td className="p-3 text-right">
                        <button data-testid={`admin-delete-${o.id}`} onClick={() => remove(o.id)} aria-label="Excluir" className="hover:text-red-700">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : recipeOrders.length === 0 ? (
          <p data-testid="recipe-empty" className="py-20 text-center text-sm uppercase tracking-[0.2em]">Nenhum pedido de receita até o momento.</p>
        ) : (
          <div className="mt-8 border border-black overflow-x-auto">
            <table className="w-full text-sm" data-testid="recipe-orders-table">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">Data</th>
                  <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">Cliente</th>
                  <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">E-mail</th>
                  <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">WhatsApp</th>
                  <th className="text-left p-3 font-medium uppercase tracking-[0.18em] text-xs">Status</th>
                  <th className="text-right p-3 font-medium uppercase tracking-[0.18em] text-xs">Valor</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {recipeOrders.map((o) => (
                  <tr key={o.id} className="border-t border-black align-top">
                    <td className="p-3 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                    <td className="p-3 font-medium">{o.name}</td>
                    <td className="p-3 whitespace-nowrap"><a href={`mailto:${o.email}`} className="underline underline-offset-4">{o.email}</a></td>
                    <td className="p-3 whitespace-nowrap">
                      {o.phone ? <a href={`https://wa.me/${o.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="underline underline-offset-4">{o.phone}</a> : "—"}
                    </td>
                    <td className="p-3">
                      {o.status === "approved" ? (
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em]"><span className="w-2 h-2 bg-black rounded-full" /> Liberado</span>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] opacity-70"><span className="w-2 h-2 border border-black rounded-full" /> Aguardando</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-display text-lg whitespace-nowrap">{fmtBRL(o.amount)}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {o.status === "approved" ? (
                        <button data-testid={`recipe-copy-${o.id}`} onClick={() => copyRecipeLink(o.download_token)} className="btn-outline !px-3 !py-1 text-[10px] mr-1">
                          <Copy size={12} /> Link
                        </button>
                      ) : (
                        <button data-testid={`recipe-approve-${o.id}`} onClick={() => approveRecipe(o.id)} className="btn-primary !px-3 !py-1 text-[10px] mr-1">
                          <Check size={12} /> Liberar
                        </button>
                      )}
                      <button data-testid={`recipe-delete-${o.id}`} onClick={() => removeRecipe(o.id)} aria-label="Excluir" className="hover:text-red-700 ml-2">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em]">{label}</p>
      <p className="font-display text-3xl md:text-4xl mt-1 tracking-[-0.02em]">{value}</p>
    </div>
  );
}
